import { useRouter } from 'next/router';
import { useEffect } from 'react';

import { adWriteApi } from 'api/adWriteApi';

import { useAdDetailsContext } from 'contexts/AdDetailsContext';
import { useUserContext } from 'contexts/UserContext';
import { useVerificationContext } from 'contexts/VerificationContext';
import { useQueryParams } from 'hooks/UseQueryParams';
import { useAuthOptions } from 'hooks/useAuthOptions';
import { useOnUpdateOnly } from 'hooks/UseOnUpdateOnly';

import { fireToast } from 'helpers/Toasts';
import { formatAuthorizationHeader } from 'helpers/auth';
import { pushToDataLayer } from 'services/gtmService';

import type { UseRevealDealerEnquiryModalArgs } from 'components/PageWrappers/SharedComponents/RevealDealerEnquiryModal/RevealDealerEnquiryModal.hook.typed';

const useRevealDealerEnquiryModal = (
  props: UseRevealDealerEnquiryModalArgs,
) => {
  const { adId, clickSource } = props;

  const { setIsDealerEnquiryModalOpen, setEnquiry } = useAdDetailsContext();
  const { accessToken, status, handleLogin } = useAuthOptions();
  const { triggerAfterVerify, setTriggerAfterVerify } =
    useVerificationContext();
  const { user } = useUserContext();
  const { query, asPath } = useRouter();
  const { addQueries, removeQueries, checkIfQueryIsPresent } = useQueryParams();

  const token = formatAuthorizationHeader(accessToken);
  const MODAL_QUERY_KEY = 'modal';
  const MODAL_QUERY_VALUE = 'dealer-enquiry';
  const MODAL_QUERY = { modal: MODAL_QUERY_VALUE };
  const isDealerEnquiryModalQueryPresent = checkIfQueryIsPresent(
    MODAL_QUERY_KEY,
    MODAL_QUERY_VALUE,
  );
  const callbackUrl = isDealerEnquiryModalQueryPresent
    ? asPath
    : `${asPath}&modal=${MODAL_QUERY_VALUE}`;

  useEffect(() => {
    if (isDealerEnquiryModalQueryPresent) {
      openModal();
    } else {
      closeModal();
    }
  }, [query, status, user]);

  const openModal = async () => {
    if (status == 'authenticated') {
      if (user?.isMinimumVerified) {
        const initialDealerEnquiryModalData =
          await initialiseDealerEnquiryModal();
        if (initialDealerEnquiryModalData) {
          // TODO: Add reducer
          setEnquiry(initialDealerEnquiryModalData);
          setIsDealerEnquiryModalOpen(true);
        }
      } else {
        removeQueries([MODAL_QUERY_KEY]);
        fireToast({
          type: 'INFO',
          text: 'You will need to verify your phone or email before sending an enquiry',
        });
      }
    } else if (status == 'unauthenticated') {
      directUserToSigninPage();
    }
  };

  const initialiseDealerEnquiryModal = async () => {
    try {
      const { data: initialValues } =
        await adWriteApi.getDealerEnquiryInitialValues(adId, token);
      return initialValues;
    } catch (_error) {
      fireToast({
        type: 'ERROR',
        text: 'Oops! Something went wrong, please try again later',
      });
    }
  };

  const directUserToSigninPage = () => {
    handleLogin(callbackUrl);
  };

  const closeModal = () => setIsDealerEnquiryModalOpen(false);

  useOnUpdateOnly(() => {
    if (triggerAfterVerify && user && user.isMinimumVerified) {
      setTriggerAfterVerify(false);
      handleReveal();
    }
  }, [triggerAfterVerify]);

  const handleReveal = () => {
    pushToDataLayer({ event: 'message_intent', source: clickSource });
    addQueries(MODAL_QUERY);
  };

  return {
    directUserToSigninPage,
    handleReveal,
  };
};

export { useRevealDealerEnquiryModal };
